import ensureError from "@-/com.npmjs.ensure-error";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * This device holds study that the account's cloud database does not, so erasing the device now
 * would lose it for good. Thrown INSTEAD of erasing; only the user's explicit choice to discard it
 * goes past this.
 */
export class UnsyncedStudyDataError extends Error {
  constructor(reason: string, options?: ErrorOptions) {
    super(`Your latest study on this device hasn't reached your account yet (${reason}).`, options);
    this.name = "UnsyncedStudyDataError";
  }
}

/** The two engine operations the proof needs (the actor's Turso sync handle has both). */
export interface UploadableActorSync {
  push(): Promise<void>;
  stats(): Promise<{ cdcOperations: number }>;
}

/**
 * Upload every local change and PROVE the cloud has them: after the push, the engine's own count
 * of unsent changes must read 0. A push that merely resolved is not that proof: a no-op stand-in
 * for a handle that never opened resolves too, and so does a push that a later write outran.
 *
 * Every failure, including "there is no cloud connection" and running out of `deadlineMs`, becomes
 * {@link UnsyncedStudyDataError}, so a caller about to erase the device cannot mistake it for a
 * routine error and erase anyway. On expiry the upload may still finish in the background, which
 * only ever leaves the cloud with more.
 */
export async function assertEveryLocalChangeUploaded(
  connect: () => Promise<UploadableActorSync | null>,
  { deadlineMs }: { deadlineMs: number },
): Promise<void> {
  try {
    await withDeadline(
      { deadline: deadlineMs, operation: "upload study data before erasing it" },
      async () => {
        const sync = await connect();
        if (sync === null) {
          throw new Error("the cloud connection did not open");
        }
        await sync.push();
        const { cdcOperations } = await sync.stats();
        if (cdcOperations > 0) {
          throw new Error(
            `${cdcOperations} ${cdcOperations === 1 ? "change is" : "changes are"} still waiting to upload`,
          );
        }
      },
    );
  } catch (error) {
    if (error instanceof UnsyncedStudyDataError) {
      throw error;
    }
    throw new UnsyncedStudyDataError(ensureError(error).message, { cause: error });
  }
}
